import { Link } from "react-router-dom";
import {
  Stethoscope,
  ClipboardCheck,
  Pill,
  FileSignature,
  ShieldCheck,
  TrendingDown,
  Zap,
  Users,
  ArrowRight,
  Check,
  Award,
  Activity,
} from "lucide-react";

export default function ForProviders() {
  return (
    <div data-testid="providers-page" className="bg-white">
      {/* HERO */}
      <section className="py-20 lg:py-28 bg-navy text-white relative overflow-hidden" data-testid="providers-hero">
        <div className="absolute top-10 right-0 w-[500px] h-[500px] bg-teal/15 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 mb-6">
              <Award className="w-3.5 h-3.5 text-teal" />
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-teal">For Clinicians</span>
            </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white">
              Less admin.<br /><span className="hl-teal">More practice.</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-white/80 leading-relaxed max-w-xl">
              Our patented backflow combined with DoseSpot, Drug Bank, pVerify, and DocuSign gives mental health
              providers a cleaner inbox, safer prescribing, and clearer consent — all inside a HIPAA-compliant platform.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/partner" className="btn-primary" data-testid="providers-partner-cta">
                Join our provider network <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/contact" className="btn-outline-teal !border-white !text-white hover:!bg-white hover:!text-navy" data-testid="providers-demo-cta">
                Request a demo
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="gh-card-dark !bg-white/5 !border-white/10 !p-8 backdrop-blur">
              <p className="eyebrow !text-teal mb-4">The clinician stack</p>
              <div className="space-y-4">
                {[
                  { icon: <ClipboardCheck className="w-5 h-5" />, name: "pVerify", desc: "Insurance eligibility in seconds" },
                  { icon: <Pill className="w-5 h-5" />, name: "DoseSpot", desc: "Certified e-prescribing" },
                  { icon: <Stethoscope className="w-5 h-5" />, name: "Drug Bank", desc: "Interaction & safety intelligence" },
                  { icon: <FileSignature className="w-5 h-5" />, name: "DocuSign", desc: "Minor & emancipated-youth consent" },
                  { icon: <Activity className="w-5 h-5" />, name: "Integrity Monitoring", desc: "Real-time fraud, waste & abuse prevention" },
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10" data-testid={`stack-${i}`}>
                    <div className="w-10 h-10 rounded-lg bg-teal/20 text-teal flex items-center justify-center flex-shrink-0">
                      {t.icon}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-white">{t.name}</p>
                      <p className="text-xs text-white/60">{t.desc}</p>
                    </div>
                    <Check className="w-4 h-4 text-teal" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="py-20 lg:py-28" data-testid="providers-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow mb-4">Why providers join</p>
            <h2 className="font-display text-4xl sm:text-5xl text-navy leading-[1.05]">
              We built the <span className="hl-teal">platform we wished we had</span> in practice.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: <TrendingDown className="w-6 h-6" />, title: "Less inbox overload", desc: "Patented backflow triages what's urgent — so your 9 a.m. coffee doesn't start with 200 messages." },
              { icon: <Zap className="w-6 h-6" />, title: "Faster patient onboarding", desc: "Insurance verified, consent signed, intake complete — before they walk into your first session." },
              { icon: <ShieldCheck className="w-6 h-6" />, title: "HIPAA-native", desc: "Encryption, access controls, and audit trails baked in. You focus on clinical work." },
              { icon: <Pill className="w-6 h-6" />, title: "Safer prescribing", desc: "DoseSpot + Drug Bank mean every prescription is screened for interactions, allergies, and alternatives." },
              { icon: <Users className="w-6 h-6" />, title: "Better continuity", desc: "AI-surfaced patient context means your notes stay warm across visits, not reset each time." },
              { icon: <Activity className="w-6 h-6" />, title: "A fraud-free network", desc: "Real-time integrity monitoring tracks every encounter on the platform — so the clinicians you sit beside are vetted, active, and clean." },
              { icon: <Award className="w-6 h-6" />, title: "Mission-aligned", desc: "You're joining a movement to make mental health care a right, not a privilege." },
            ].map((v, i) => (
              <div key={i} className="gh-card" data-testid={`provider-value-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-teal-light text-teal flex items-center justify-center mb-5">
                  {v.icon}
                </div>
                <h3 className="font-display text-xl mb-2 text-navy">{v.title}</h3>
                <p className="text-muted-ink text-[15px] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BACKFLOW CALLOUT */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="backflow-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="bg-gradient-navy rounded-[2.5rem] p-10 lg:p-16 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-teal/20 rounded-full blur-3xl" aria-hidden="true" />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="eyebrow !text-teal mb-4">Patented technology</p>
                <h2 className="font-display text-4xl sm:text-5xl text-white leading-[1.05] mb-5">
                  The provider <span className="hl-teal">backflow.</span>
                </h2>
                <p className="text-lg text-white/80 leading-relaxed mb-6">
                  Traditional EHR inboxes treat every message equally. Our patented backflow evaluates
                  urgency, emotional valence, and clinical priority — then routes the right signal to the
                  right clinician at the right moment. Human-in-the-loop by design, quiet by default.
                </p>
                <Link to="/contact" className="btn-primary" data-testid="backflow-learn-more">
                  Learn how it works <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="space-y-3">
                {[
                  { step: "01", text: "Patient touchpoint enters system" },
                  { step: "02", text: "AI evaluates urgency & context" },
                  { step: "03", text: "Backflow routes to appropriate provider" },
                  { step: "04", text: "Provider sees one clear, clinical signal" },
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/10" data-testid={`backflow-step-${i}`}>
                    <span className="font-display text-3xl text-teal">{s.step}</span>
                    <p className="text-white pt-1 font-medium">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" data-testid="providers-cta">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-navy mb-5 leading-[1.1]">
            Bring your practice <span className="hl-teal">into the movement.</span>
          </h2>
          <p className="text-lg text-muted-ink mb-8">
            We're onboarding licensed mental health providers now. Let's talk about fit.
          </p>
          <Link to="/partner" className="btn-primary" data-testid="providers-final-cta">
            Apply to join <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
